import { useState, useEffect } from "react";
import { useAppSelector } from "../../../store";
import { storedLocale } from "../../Authentication/util";
import { useGetSaqQuery } from "../../../services/ActionReports/ActionReportAPI";
import type { SaqActionReportResponse } from "./SaqActionReportMockData";
import type { GroupLeaderNode } from "../../../services/ActionReports/ActionReportAPI.types";
import { useWatchlistFetch } from "../hooks/useWatchlistFetch";

const defaultSaqResponse: SaqActionReportResponse = {
  showUpForSAQRenewal: false,
  saq: [],
  groupLeaders: [],
  groupLeaderTree: [],
  error: null,
};

/**
 * Data layer for SAQ Action Report.
 *
 * API endpoint: GET /acc-action-report-v2-api/api/saq?aff=&abo=&period=&locale=&timezone=
 *
 * Architecture:
 * - SAQ API returns a flat list of ABOs + group leaders, with `completed`,
 *   `dueDate`, and `upForRenewal` fields per record, and `showUpForSAQRenewal`
 *   at the response root.
 * - Tab filtering (completed/incomplete/upForRenewal) is driven by the API-provided
 *   `completed` and `upForRenewal` fields directly.
 * - ABO Enrichment API provides: sponsoringYtd, groupSize, annualPPV,
 *   phoneNumber, profileImage (via useLazyAboEnrichment in DesktopTable).
 */
export const FetchSaqActionReportData = (period: string, skipRequest = false) => {
  const { user } = useAppSelector((state) => state.boot);

  const {
    data: apiData,
    isFetching,
    isError,
    refetch,
  } = useGetSaqQuery(
    {
      Aff: user.aff,
      Abo: user.abo,
      Period: period,
      Locale: storedLocale(),
      TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    {
      skip: skipRequest,
    },
  );

  const { isLoading: isLoadingWatchlist } = useWatchlistFetch();

  const [isLoading, setIsLoading] = useState(true);
  const [saqActionReportData, setSaqActionReportData] = useState<SaqActionReportResponse>(defaultSaqResponse);

  useEffect(() => {
    // Only stop loading when both report data AND watchlist are ready
    setIsLoading(isFetching || isLoadingWatchlist);

    if (!isFetching && !isError && apiData) {
      const responseData: SaqActionReportResponse = {
        showUpForSAQRenewal: apiData.showUpForSAQRenewal ?? false,
        saq: apiData.saq ?? [],
        groupLeaders: apiData.groupLeaders ?? [],
        groupLeaderTree:
          (apiData as unknown as { groupLeaderTree?: GroupLeaderNode[] }).groupLeaderTree ??
          (apiData.groupLeaders ?? []).map((gl) => ({ ...gl, children: [] })),
        error: null,
      };

      setSaqActionReportData(responseData);
    }
  }, [isFetching, isError, apiData, isLoadingWatchlist]);

  return { isLoading, saqActionReportData, errorStatus: isError, refetch };
};
